import { useEffect, useMemo, useState } from 'react';
import type { CwDataset, CwSyncOutcome, CwSyncSummary } from '@catalyst-psa/shared';
import { CW_DATASET_LABELS } from '@catalyst-psa/shared';

import { Modal } from '../../components/ui/Modal.js';
import { Button } from '../../components/ui/Button.js';
import { Badge } from '../../components/ui/Badge.js';
import { SelectField, TextField } from '../../components/ui/Field.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useProjects } from '../projects/api.js';
import { useAgreements } from '../agreements/api.js';
import {
  useCwDatasetSample,
  useImportAllCwDataset,
  useTestSyncByIdCwDataset,
  useTestSyncByInvoiceNumberCwDataset,
  useTestSyncCwDataset,
} from './api.js';

interface DatasetTestModalProps {
  dataset: CwDataset;
  isOpen: boolean;
  onClose: () => void;
}

const SAMPLE_LIMIT = 10;

const OUTCOME_TONES: Record<CwSyncOutcome, 'green' | 'amber' | 'red' | 'blue'> = {
  ok: 'green',
  partial: 'amber',
  failed: 'red',
  running: 'blue',
};

function SummaryPanel({ summary }: { summary: CwSyncSummary }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="mb-1 flex items-center gap-2">
        <Badge tone={OUTCOME_TONES[summary.outcome]}>{summary.outcome}</Badge>
        <span className="text-slate-600">
          created {summary.created}, updated {summary.updated}, unchanged {summary.unchanged} of{' '}
          {summary.fetched} selected
        </span>
      </div>
      {summary.message ? <p className="text-xs text-slate-500">{summary.message}</p> : null}
    </div>
  );
}

/**
 * Narrows a time-entry sample to a single project or agreement under the selected
 * customer, so a specific scenario can be tested without scrolling the global feed.
 * Rendered only once a customer is chosen, so its project/agreement lookups stay scoped.
 */
function SampleLinkPicker({
  customerId,
  projectId,
  agreementId,
  onProjectChange,
  onAgreementChange,
}: {
  customerId: string;
  projectId: string;
  agreementId: string;
  onProjectChange: (id: string) => void;
  onAgreementChange: (id: string) => void;
}): JSX.Element {
  const projectsQuery = useProjects({ page: 1, pageSize: 100, customerId });
  const agreementsQuery = useAgreements({ page: 1, pageSize: 100, customerId });
  const projects = projectsQuery.data?.data ?? [];
  const agreements = agreementsQuery.data?.data ?? [];

  const projectOptions = [
    { value: '', label: 'Any project' },
    ...projects.map((p) => ({ value: p.id, label: `#${p.number} · ${p.name}` })),
  ];
  const agreementOptions = [
    { value: '', label: 'Any agreement' },
    ...agreements.map((a) => ({ value: a.id, label: `#${a.number} · ${a.name}` })),
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <SelectField
        label="Project"
        value={projectId}
        options={projectOptions}
        onChange={(e) => onProjectChange(e.target.value)}
      />
      <SelectField
        label="Agreement"
        value={agreementId}
        options={agreementOptions}
        onChange={(e) => onAgreementChange(e.target.value)}
      />
    </div>
  );
}

export function DatasetTestModal({ dataset, isOpen, onClose }: DatasetTestModalProps): JSX.Element {
  const supportsLinkFilter = dataset === 'timeEntries' || dataset === 'invoices';
  const [customerId, setCustomerId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [agreementId, setAgreementId] = useState('');

  const sampleQuery = useCwDatasetSample(dataset, SAMPLE_LIMIT, isOpen, {
    projectId: projectId || undefined,
    agreementId: agreementId || undefined,
    customerId: customerId || undefined,
  });
  const testSync = useTestSyncCwDataset();
  const { reset: resetTest } = testSync;
  const importAll = useImportAllCwDataset();
  const { reset: resetImportAll } = importAll;
  const syncById = useTestSyncByIdCwDataset();
  const { reset: resetSyncById } = syncById;
  const syncByNumber = useTestSyncByInvoiceNumberCwDataset();
  const { reset: resetSyncByNumber } = syncByNumber;
  const [cwRecordId, setCwRecordId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const records = useMemo(() => sampleQuery.data?.records ?? [], [sampleQuery.data]);

  // Reset selection and any previous result whenever the modal (re)opens or the
  // sample changes, so a stale summary never lingers across runs.
  useEffect(() => {
    if (isOpen) {
      setSelected(new Set());
      resetTest();
      resetImportAll();
      resetSyncById();
      resetSyncByNumber();
    }
  }, [isOpen, records, resetTest, resetImportAll]);

  // Clear the scenario filter and the direct-id input when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      setCustomerId('');
      setProjectId('');
      setAgreementId('');
      setCwRecordId('');
      setInvoiceNumber('');
    }
  }, [isOpen]);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allSelected = records.length > 0 && selected.size === records.length;
  const toggleAll = (): void => {
    setSelected(allSelected ? new Set() : new Set(records.map((r) => r.id)));
  };

  const handleRun = (): void => {
    if (selected.size === 0) return;
    testSync.mutate({
      dataset,
      input: {
        ids: [...selected],
        projectId: projectId || undefined,
        agreementId: agreementId || undefined,
        customerId: customerId || undefined,
      },
    });
  };

  const hasLinkSelected = Boolean(
    projectId || agreementId || (dataset === 'invoices' && customerId),
  );
  const handleImportAll = (): void => {
    if (!hasLinkSelected) return;
    importAll.mutate({
      dataset,
      input: {
        projectId: projectId || undefined,
        agreementId: agreementId || undefined,
        customerId: dataset === 'invoices' ? customerId || undefined : undefined,
      },
    });
  };

  const trimmedRecordId = cwRecordId.trim();
  const handleSyncById = (): void => {
    if (!trimmedRecordId) return;
    syncById.mutate({ dataset, input: { cwRecordId: trimmedRecordId } });
  };

  const trimmedInvoiceNumber = invoiceNumber.trim();
  const handleSyncByNumber = (): void => {
    if (!trimmedInvoiceNumber) return;
    syncByNumber.mutate({ invoiceNumber: trimmedInvoiceNumber });
  };

  // Column keys come from the first record's mapped values (all records share the
  // dataset's mapping, so the key set is stable).
  const columns = records[0] ? Object.keys(records[0].values) : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Test ${CW_DATASET_LABELS[dataset]} sync`}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-xs text-slate-500">
            {selected.size} of {records.length} selected
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={handleRun}
              isLoading={testSync.isPending}
              disabled={selected.size === 0}
            >
              Run selected
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-500">
          A few recent ConnectWise records are shown below with their mapped values. Select the ones
          you want and run a real upsert for just those, so you can verify the mapping before a full
          sync.
        </p>

        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Force-sync one record by CW ID
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <TextField
                label="ConnectWise record id"
                value={cwRecordId}
                onChange={(e) => setCwRecordId(e.target.value)}
                placeholder="e.g. 444"
                inputMode="numeric"
              />
            </div>
            <Button
              variant="secondary"
              onClick={handleSyncById}
              isLoading={syncById.isPending}
              disabled={!trimmedRecordId}
            >
              Sync this id
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Fetches and upserts just this record, ignoring the customer filter and change date.
          </p>
          {syncById.isSuccess && syncById.data ? <SummaryPanel summary={syncById.data} /> : null}
          {syncById.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Sync failed. Please try again.
            </div>
          ) : null}
        </div>

        {dataset === 'invoices' ? (
          <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Force-sync one invoice by number
            </p>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <TextField
                  label="ConnectWise invoice number"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="e.g. 123456"
                />
              </div>
              <Button
                variant="secondary"
                onClick={handleSyncByNumber}
                isLoading={syncByNumber.isPending}
                disabled={!trimmedInvoiceNumber}
              >
                Sync this invoice
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Fetches and upserts just this invoice by its displayed number (not the RecID).
            </p>
            {syncByNumber.isSuccess && syncByNumber.data ? (
              <SummaryPanel summary={syncByNumber.data} />
            ) : null}
            {syncByNumber.isError ? (
              <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                Sync failed. Please try again.
              </div>
            ) : null}
          </div>
        ) : null}

        {supportsLinkFilter ? (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Filter to a scenario
            </p>
            <CustomerSelectField
              label="Customer"
              value={customerId}
              onChange={(id) => {
                setCustomerId(id);
                setProjectId('');
                setAgreementId('');
              }}
              allowClear
            />
            {customerId ? (
              <SampleLinkPicker
                customerId={customerId}
                projectId={projectId}
                agreementId={agreementId}
                onProjectChange={(id) => {
                  setProjectId(id);
                  if (id) setAgreementId('');
                }}
                onAgreementChange={(id) => {
                  setAgreementId(id);
                  if (id) setProjectId('');
                }}
              />
            ) : null}
            {hasLinkSelected ? (
              <div className="flex flex-col gap-2 border-t border-slate-200 pt-3">
                <p className="text-xs text-slate-500">
                  Import every {dataset === 'invoices' ? 'invoice' : 'time entry'} for the selected{' '}
                  {projectId ? 'project' : agreementId ? 'agreement' : 'customer'}, not just the
                  sample below.
                </p>
                <div>
                  <Button
                    variant="secondary"
                    onClick={handleImportAll}
                    isLoading={importAll.isPending}
                  >
                    {dataset === 'invoices' ? 'Import all invoices' : 'Import all hours'}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {importAll.isSuccess && importAll.data ? <SummaryPanel summary={importAll.data} /> : null}
        {importAll.isError ? (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            Import failed. Please try again.
          </div>
        ) : null}

        {testSync.isSuccess && testSync.data ? <SummaryPanel summary={testSync.data} /> : null}
        {testSync.isError ? (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            Test run failed. Please try again.
          </div>
        ) : null}

        {sampleQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading sample…</p>
        ) : sampleQuery.isError ? (
          <p className="text-sm text-rose-600">Failed to load the sample from ConnectWise.</p>
        ) : !sampleQuery.data?.configured ? (
          <p className="text-sm text-slate-500">
            Configure the ConnectWise connection to load sample records.
          </p>
        ) : !sampleQuery.data.implemented ? (
          <p className="text-sm text-slate-500">A test sync is not available for this dataset.</p>
        ) : records.length === 0 ? (
          <p className="text-sm text-slate-400">No records were returned from ConnectWise.</p>
        ) : (
          <div className="max-h-[55vh] overflow-auto rounded-lg border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all"
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Record</th>
                  {columns.map((col) => (
                    <th key={col} className="px-3 py-2 font-medium">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((record) => (
                  <tr
                    key={record.id}
                    className={selected.has(record.id) ? 'bg-brand-50/60' : undefined}
                  >
                    <td className="px-3 py-2 align-top">
                      <input
                        type="checkbox"
                        checked={selected.has(record.id)}
                        onChange={() => toggle(record.id)}
                        aria-label={`Select ${record.label}`}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className="font-medium text-slate-800">{record.label}</span>
                      <span className="block text-xs text-slate-400">#{record.id}</span>
                    </td>
                    {columns.map((col) => (
                      <td key={col} className="px-3 py-2 align-top text-slate-700">
                        {record.values[col] ?? <span className="text-slate-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
